
import { useState } from 'react';
import Button from '../components/base/Button';
import Input from '../components/base/Input';
import Select from '../components/base/Select';
import Toast from '../components/base/Toast';

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    fecha: '',
    mensaje: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const serviceOptions = [
    { value: 'residencial', label: 'Residencial' },
    { value: 'comercial', label: 'Comercial' },
    { value: 'profunda', label: 'Limpieza Profunda' },
    { value: 'move-in-out', label: 'Move-in/Move-out' },
    { value: 'post-construccion', label: 'Post-construcción' },
    { value: 'eco-friendly', label: 'Eco-friendly' }
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ingresa un email válido';
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.telefono)) {
      newErrors.telefono = 'Ingresa un teléfono válido';
    }

    if (!formData.servicio) {
      newErrors.servicio = 'Selecciona un tipo de servicio';
    }

    if (formData.mensaje.length > 500) {
      newErrors.mensaje = 'El mensaje no puede exceder 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare form data for submission
      const submitData = new URLSearchParams();
      submitData.append('nombre', formData.nombre);
      submitData.append('email', formData.email);
      submitData.append('telefono', formData.telefono);
      submitData.append('servicio', formData.servicio);
      submitData.append('fecha', formData.fecha);
      submitData.append('mensaje', formData.mensaje);

      // Submit to the form URL
      await fetch('https://readdy.ai/api/form/d472j4ihchb23d4efig0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString()
      });

      // Reset form and show success message
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        servicio: '',
        fecha: '',
        mensaje: ''
      });
      setErrors({});
      setShowToast(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <section id="contacto" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-outfit">
            Agenda tu limpieza hoy
          </h2>
          <p className="text-xl text-slate-600 font-inter">
            Completa el formulario y te contactaremos para confirmar tu cita
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-8 md:p-12">
          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="cotizacion-limpieza">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Nombre completo *"
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={(e) => handleInputChange('nombre', e.target.value)}
                error={errors.nombre}
                placeholder="Tu nombre completo"
                required
              />
              
              <Input
                label="Email *"
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={errors.email}
                placeholder="tu@email.com"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Teléfono *"
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={(e) => handleInputChange('telefono', e.target.value)}
                error={errors.telefono}
                placeholder="(555) 123-4567"
                required
              />
              
              <Select
                label="Tipo de servicio *"
                name="servicio"
                value={formData.servicio}
                onChange={(e) => handleInputChange('servicio', e.target.value)}
                error={errors.servicio}
                options={serviceOptions}
                placeholder="Selecciona un servicio"
                required
              />
            </div>

            <Input
              label="Fecha preferida"
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={(e) => handleInputChange('fecha', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />

            <div>
              <label htmlFor="mensaje" className="block text-sm font-medium text-slate-700 mb-2">
                Mensaje adicional
              </label>
              <textarea
                id="mensaje"
                name="mensaje"
                value={formData.mensaje}
                onChange={(e) => handleInputChange('mensaje', e.target.value)}
                placeholder="Cuéntanos sobre áreas específicas que requieren atención..."
                rows={4}
                maxLength={500}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors resize-none ${
                  errors.mensaje 
                    ? 'border-red-500 bg-red-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                aria-describedby={errors.mensaje ? 'mensaje-error' : undefined}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.mensaje && (
                  <p id="mensaje-error" className="text-sm text-red-600" role="alert">
                    {errors.mensaje}
                  </p>
                )}
                <p className="text-sm text-gray-500 ml-auto">
                  {formData.mensaje.length}/500 caracteres
                </p>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Enviando...' : 'Solicitar cotización'}
            </Button>
          </form>
        </div>
      </div>

      <Toast
        message="¡Gracias! Tu solicitud ha sido enviada."
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </section>
  );
}
